import type { TimescaleGraphTemplate } from "../graphTemplates"
import type { TimescaleMonitoringGraphPayload } from "../hooks/useMonitoringGraphs"

/**
 * Filter monitoring graph templates by available asset classes.
 *
 * Templates without a class filter (role-only or no filter) are always included.
 * Templates with a class filter are only included if that class exists in the
 * available set — ensuring the view only shows relevant graphs.
 */
export function filterGraphsByAvailableClasses(
  templates: TimescaleGraphTemplate[],
  availableClasses: string[] | undefined
): TimescaleGraphTemplate[] {
  if (!availableClasses) return templates

  const classSet = new Set(availableClasses)

  return templates.filter((template) => {
    // Templates with no class filter are always shown (e.g. role-based like "generation")
    if (!template.filter?.class) return true
    // Templates with a class filter are shown only if that class is available
    return classSet.has(template.filter.class)
  })
}

/**
 * Check if a monitoring graph payload has any renderable data.
 * Returns false if both breakdown and aggregated data are empty.
 *
 * This is a lightweight check for the shared rendering layer — it verifies
 * that at least one data array is non-empty. The systems section has a deeper
 * `batchPayloadHasNumericData` that also checks for finite numeric values;
 * this utility covers the common case of entirely empty responses.
 */
export function hasGraphData(payload: TimescaleMonitoringGraphPayload): boolean {
  const hasBreakdown = payload.breakdown.data.length > 0
  const hasAggregated = Boolean(
    payload.aggregated && payload.aggregated.data.length > 0
  )
  return hasBreakdown || hasAggregated
}
